<script setup lang="ts">
import {
  computed,
  ref,
} from 'vue';
import { useRoute } from 'vue-router';

import type { IBalancesEditFields } from '@modules/balances/submodules/edit/types';
import type {
  FormSchema,
  VFormRef,
} from '@modules/forms/components/VForm/types';
import VBackButton from '@components/buttons/VBackButton/VBackButton.vue';
import VCard from '@components/card/VCard/VCard.vue';
import VLoader from '@components/loader/VLoader.vue';
import {
  ADMIN_BALANCES_LIST_ROUTE_NAME,
  TRANSLATION_GROUP_BALANCES,
} from '@modules/balances/constants';
import { useBalancesEditMutation } from '@modules/balances/submodules/edit/mutations';
import { useBalancesItemQuery } from '@modules/balances/submodules/edit/queries';
import { useConfirmDialogs } from '@modules/core/composables/dialogs';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { EFormInputTypes } from '@modules/forms/enums';
import { useI18n } from '@modules/i18n/composables';
import { isString } from '@utils/guards';

const route = useRoute();
const id = computed<number>(() => (
  isString(route.params.id) ? Number(route.params.id) : 0
));

const { getTranslation } = useI18n();
const { confirmEdit } = useConfirmDialogs();

const { data: balancesItem, isLoading, refetch } = useBalancesItemQuery(id);
const {
  mutateAsync,
  isLoading: isLoadingSave,
  errors,
  clearError,
} = useBalancesEditMutation();

const schema = computed<FormSchema<IBalancesEditFields>>(() => ({
  affiliate: {
    type: EFormInputTypes.TEXT,
    readonly: true,
    label: getTranslation(TRANSLATION_GROUP_BALANCES, 'FORM_AFFILIATE').value,
    value: `${balancesItem.value?.id} - ${balancesItem.value?.email} - ${balancesItem.value?.username}`,
  },
  current_balance: {
    type: EFormInputTypes.NUMBER,
    readonly: true,
    label: getTranslation(TRANSLATION_GROUP_BALANCES, 'FORM_CURRENT_BALANCE').value,
    value: balancesItem.value?.amount,
  },
  amount: {
    type: EFormInputTypes.NUMBER,
    label: getTranslation(TRANSLATION_GROUP_BALANCES, 'FORM_BALANCE_AMOUNT').value,
  },
  comment: {
    type: EFormInputTypes.TEXTAREA,
    label: getTranslation(TRANSLATION_GROUP_BALANCES, 'FORM_COMMENT').value,
  },
}));

const form = ref<VFormRef>();
function onSubmit(value: IBalancesEditFields): void {
  confirmEdit({
    header: getTranslation(TRANSLATION_GROUP_BALANCES, 'MODAL_TITLE').value,
    message: getTranslation(TRANSLATION_GROUP_BALANCES, 'MODAL_QUESTION').value,
    acceptLabel: getTranslation(TRANSLATION_GROUP_BALANCES, 'MODAL_YES').value,
    rejectLabel: getTranslation(TRANSLATION_GROUP_BALANCES, 'MODAL_NO').value,
    accept: () => {
      void mutateAsync({
        ...value,
        id: id.value,
      }).then(() => {
        form.value?.reset();
        void refetch();
      });
    },
  });
}
</script>

<template>
  <VCard v-data-qa-auto-id="'BalancesEditPage'"
    type="form"
    :title="$getTranslation(TRANSLATION_GROUP_BALANCES, 'ADJUSTMENT_HEADER')"
  >
    <VLoader
      min-height="200px"
      :loading="isLoading"
    >
      <VForm
        ref="form"
        :schema="schema"
        :submit-btn-label="$getTranslation(TRANSLATION_GROUP_BALANCES, 'ADJUSTMENT_SAVE')"
        :is-loading="isLoadingSave"
        :custom-errors="errors"
        @interacted="clearError"
        @submit="onSubmit"
      >
        <template #secondaryBtn>
          <VBackButton
            :to="{
              name: ADMIN_BALANCES_LIST_ROUTE_NAME,
            }"
            :label="$getTranslation(TRANSLATION_GROUP_BALANCES, 'ADJUSTMENT_BACK')"
          />
        </template>
      </VForm>
    </VLoader>
  </VCard>
</template>
